// Searching and grouping a collected directory.
//
// Two decisions shape this, and both come from the same observation: the only
// data available is what was on somebody else's card.
//
// 1. Industries are DERIVED, not declared. A picker in the editor would be
//    filled in by almost nobody, so grouping by it would sort a directory into
//    one big "unspecified" pile. Instead each card's industry is inferred from
//    the words already on it — the job title and the company. An industry the
//    card states explicitly always wins; this only fills the gap.
//
// 2. The list of industries offered comes from THIS person's collection, never
//    a fixed global set. Somebody who only collects architects should not be
//    offered a Healthcare filter that matches nothing.
//
// Matching is deliberately shallow — keywords, not a model. It runs on every
// keystroke, offline, on a phone, and a wrong guess costs a card being in a
// slightly odd group, which is far cheaper than a slow or heavyweight one.

/** A derived industry: a stable key and the label people see. */
export interface Industry { key: string; label: string }

/** Keyword table, most specific first — the first match wins, so "real estate"
 *  must be tested before "estate" and "art director" before "director". */
const RULES: { key: string; label: string; words: string[] }[] = [
  { key: 'technology', label: 'Technology', words: ['software', 'engineer', 'developer', 'devops', 'programmer', 'cto', 'sre', 'data scientist', 'machine learning', 'ai ', 'it ', 'cyber', 'security', 'cloud', 'saas', 'tech', 'digital', 'web'] },
  { key: 'design', label: 'Design', words: ['design', 'ux', 'ui', 'creative director', 'art director', 'illustrat', 'brand', 'architect', 'interior'] },
  { key: 'marketing', label: 'Marketing', words: ['marketing', 'growth', 'seo', 'content', 'copywriter', 'social media', 'pr ', 'communications', 'advertis'] },
  { key: 'finance', label: 'Finance', words: ['finance', 'financial', 'account', 'audit', 'bank', 'invest', 'capital', 'cfo', 'tax', 'wealth', 'insurance', 'venture'] },
  { key: 'legal', label: 'Legal', words: ['law', 'legal', 'attorney', 'solicitor', 'barrister', 'counsel', 'paralegal', 'notary'] },
  { key: 'healthcare', label: 'Healthcare', words: ['health', 'medical', 'doctor', 'dr.', 'nurse', 'dental', 'dentist', 'clinic', 'therapist', 'physio', 'pharma', 'surgeon', 'veterinar'] },
  { key: 'education', label: 'Education', words: ['teacher', 'professor', 'lecturer', 'tutor', 'school', 'university', 'college', 'academy', 'education', 'training', 'coach'] },
  { key: 'realestate', label: 'Real estate', words: ['real estate', 'realtor', 'property', 'estate agent', 'letting', 'mortgage', 'surveyor'] },
  { key: 'hospitality', label: 'Hospitality', words: ['restaurant', 'chef', 'hotel', 'catering', 'cafe', 'bar ', 'hospitality', 'travel', 'tour', 'event'] },
  { key: 'construction', label: 'Construction', words: ['construction', 'builder', 'contractor', 'plumb', 'electric', 'carpenter', 'roofing', 'renovation', 'joiner'] },
  { key: 'manufacturing', label: 'Manufacturing', words: ['manufactur', 'factory', 'industrial', 'engineering ltd', 'production', 'fabricat', 'machining'] },
  { key: 'retail', label: 'Retail', words: ['retail', 'shop', 'store', 'boutique', 'merchandis', 'ecommerce', 'e-commerce'] },
  { key: 'media', label: 'Media', words: ['photograph', 'video', 'film', 'media', 'journalist', 'editor', 'producer', 'music', 'podcast', 'broadcast'] },
  { key: 'nonprofit', label: 'Nonprofit', words: ['nonprofit', 'non-profit', 'charity', 'foundation', 'ngo', 'volunteer'] },
];

const LABELS: Record<string, string> = Object.fromEntries(RULES.map((r) => [r.key, r.label]));
LABELS.other = 'Other';

/** What a card is about, from what it says. An explicit value wins. */
export function industryOf(card: { industry?: string; title?: string; company?: string }): string {
  if (card.industry && LABELS[card.industry]) return card.industry;
  const hay = `${card.title ?? ''} ${card.company ?? ''}`.toLowerCase();
  if (hay.trim()) {
    for (const rule of RULES) {
      if (rule.words.some((w) => hay.includes(w))) return rule.key;
    }
  }
  return 'other';
}

/** The industries actually present in this person's collection, in the table's
 *  order so the filter row does not reshuffle as cards are collected. "Other"
 *  sorts last: it is a remainder, not a category. */
export function industriesIn(cards: { industry?: string; title?: string; company?: string }[]): Industry[] {
  const present = new Set(cards.map(industryOf));
  const out = RULES.filter((r) => present.has(r.key)).map((r) => ({ key: r.key, label: r.label }));
  if (present.has('other')) out.push({ key: 'other', label: LABELS.other });
  return out;
}

/** Label for a derived industry key. */
export function industryLabel(key: string): string {
  return LABELS[key] ?? key;
}

/**
 * Does `card` match what was typed?
 *
 * Forgiving in the ways that actually happen when you half-remember somebody:
 * terms may be in any order and across any field ("rios meridian"), each term
 * need only match a PREFIX of some word ("ros" finds "Ríos"), and accents are
 * ignored so "rios" finds "Ríos".
 *
 * Not a full fuzzy match — no edit distance, so "reos" will not find "Ríos".
 * Prefix matching catches the common case (you stopped typing) without the
 * false positives that make an edit-distance search feel random on short
 * strings like names.
 */
export function matches(card: Record<string, unknown>, query: string): boolean {
  const terms = fold(query).split(/\s+/).filter(Boolean);
  if (!terms.length) return true;

  // `note` is the searcher's OWN words about the card. It is included because
  // it is usually the only place the thing they actually remember is written
  // down — "the woman from the Dhaka expo" is how somebody looks for a card
  // whose name they have forgotten, which is the entire reason for keeping one.
  const hay = fold([
    card.name, card.title, card.company, card.email, card.website, card.address,
    card.note,
    ...(Array.isArray(card.tags) ? (card.tags as string[]) : []),
  ].filter((v) => typeof v === 'string').join(' '));
  const words = hay.split(/[^a-z0-9]+/).filter(Boolean);

  // Every term must match something — narrowing as you type is the point.
  return terms.every((t) => words.some((w) => w.startsWith(t)) || hay.includes(t));
}

/** Lowercase and strip accents, so "Ríos" and "rios" are the same string. */
function fold(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}
